import { container } from './di/container';
import { ICollidable, CollisionUtils } from './collision/types';
import { PhysicsObject } from './physics/physics-object';
import { Cat } from './cat';
import { Pit } from './pit';
import { GameTime } from './game-time';

export interface PoopFrames {
	idle: p5.Image[]; // Row 1: 8 frames
	falling: p5.Image[]; // Row 2: frames 0-5
	landing: p5.Image[]; // Row 2: frames 6-7
}

export class Poop extends PhysicsObject {
	override readonly collisionType = 'poop';

	// Animation frames
	private idleFrames: p5.Image[] = [];
	private fallingFrames: p5.Image[] = [];
	private landingFrames: p5.Image[] = [];
	private currentFrame: number = 0;
	private frameCounter: number = 0;
	private frameSpeed: number = 6;

	// State
	private isLoaded: boolean = false;
	private hasDealtDamage: boolean = false;
	private isDestroyed: boolean = false;
	private spawnTime: number = 0;
	private animationPhase: 'idle' | 'falling' | 'waiting' | 'landing' | 'done' = 'idle';
	private static readonly GRACE_PERIOD_SECONDS = 0.5;

	constructor(x: number, y: number, groundY: number) {
		super(x, y, 32, 32, groundY);
	}

	static async loadSprite(): Promise<PoopFrames> {
		return new Promise((resolve) => {
			loadImage('./assets/models/poop.png', (img) => {
				const idle: p5.Image[] = [];
				const falling: p5.Image[] = [];
				const landing: p5.Image[] = [];
				const frameWidth = 32;
				const frameHeight = 32;

				// Row 1 (y=0): Idle animation, 8 frames
				for (let i = 0; i < 8; i++) {
					const frame = createImage(frameWidth, frameHeight);
					frame.copy(
						img,
						i * frameWidth,
						0,
						frameWidth,
						frameHeight,
						0,
						0,
						frameWidth,
						frameHeight
					);
					idle.push(frame);
				}

				// Row 2 (y=32): Falling (0-5) + Landing (6-7)
				for (let i = 0; i < 6; i++) {
					const frame = createImage(frameWidth, frameHeight);
					frame.copy(
						img,
						i * frameWidth,
						frameHeight,
						frameWidth,
						frameHeight,
						0,
						0,
						frameWidth,
						frameHeight
					);
					falling.push(frame);
				}

				for (let i = 6; i < 8; i++) {
					const frame = createImage(frameWidth, frameHeight);
					frame.copy(
						img,
						i * frameWidth,
						frameHeight,
						frameWidth,
						frameHeight,
						0,
						0,
						frameWidth,
						frameHeight
					);
					landing.push(frame);
				}

				resolve({ idle, falling, landing });
			});
		});
	}

	setFrames(frames: PoopFrames): void {
		this.idleFrames = frames.idle;
		this.fallingFrames = frames.falling;
		this.landingFrames = frames.landing;
		this.isLoaded = true;
		this.spawnTime = container.resolve(GameTime).elapsedTime;

		// Determine initial animation based on spawn position
		const groundLimit = this.groundY - this.height + this.getGroundOffset();
		if (this.y >= groundLimit - 2) {
			// Spawned on or near ground → idle
			this.animationPhase = 'idle';
		} else {
			// Spawned in air → falling
			this.animationPhase = 'falling';
		}
	}

	protected override onPitBottom(): void {
		this.isDestroyed = true;
	}

	isActive(): boolean {
		return this.isLoaded && !this.isDestroyed;
	}

	protected override getGroundOffset(): number {
		return 4;
	}

	private isInGracePeriod(): boolean {
		const gameTime = container.resolve(GameTime);
		const timeSinceSpawn = gameTime.elapsedTime - this.spawnTime;
		return timeSinceSpawn < Poop.GRACE_PERIOD_SECONDS;
	}

	onCollision(other: ICollidable): void {
		if (other instanceof Cat && !this.hasDealtDamage && !this.isDestroyed) {
			// Grace period only for cat damage, not pit collision
			if (this.isInGracePeriod()) return;

			// Check if cat's center is inside poop bounds
			const catBounds = other.getBounds();
			const poopBounds = this.getBounds();

			if (!CollisionUtils.centerInBounds(catBounds, poopBounds)) {
				return;
			}

			// Only deal damage once per poop
			if (!other.isPlayingDeathAnimation && !other.isDeathAnimationComplete) {
				other.takeDamage(1);
				this.hasDealtDamage = true;
				this.isDestroyed = true;
			}
		} else if (other.collisionType === 'pit') {
			this.handlePitCollision(other as Pit);
		}
	}

	update(): void {
		if (!this.isLoaded || this.isDestroyed) return;

		// Always apply physics - handles gravity and pit falling
		this.applyPhysics();

		// Switch to falling animation when entering pit
		if (this.isInPit && this.animationPhase !== 'falling' && this.animationPhase !== 'waiting') {
			this.animationPhase = 'falling';
			this.currentFrame = 0;
		}

		this.updateAnimation();
	}

	private updateAnimation(): void {
		// Falling animasyonu daha hızlı
		const speed = this.animationPhase === 'falling' ? 3 : this.frameSpeed;

		this.frameCounter++;
		if (this.frameCounter >= speed) {
			this.frameCounter = 0;

			switch (this.animationPhase) {
				case 'idle':
					// Loop idle animation
					if (this.idleFrames.length > 0) {
						this.currentFrame = (this.currentFrame + 1) % this.idleFrames.length;
					}
					break;

				case 'falling':
					// Animate falling frames 0-5
					if (this.currentFrame < this.fallingFrames.length - 1) {
						this.currentFrame++;
					} else {
						// Reached last falling frame, wait for ground
						this.animationPhase = 'waiting';
					}
					break;

				case 'waiting':
					// Stay on last falling frame, check if grounded
					this.currentFrame = this.fallingFrames.length - 1;
					if (this.isGrounded) {
						this.animationPhase = 'landing';
						this.currentFrame = 0;
					}
					break;

				case 'landing':
					// Animate landing frames
					if (this.currentFrame < this.landingFrames.length - 1) {
						this.currentFrame++;
					} else {
						// Done, stay on last frame
						this.animationPhase = 'done';
					}
					break;

				case 'done':
					// Stay on last landing frame forever
					this.currentFrame = this.landingFrames.length - 1;
					break;
			}
		}
	}

	draw(): void {
		if (!this.isLoaded || this.isDestroyed) return;

		let frames: p5.Image[];
		switch (this.animationPhase) {
			case 'idle':
				frames = this.idleFrames;
				break;
			case 'falling':
			case 'waiting':
				frames = this.fallingFrames;
				break;
			case 'landing':
			case 'done':
				frames = this.landingFrames;
				break;
			default:
				frames = this.idleFrames;
		}

		if (frames.length === 0) return;

		const frameIndex = Math.min(this.currentFrame, frames.length - 1);
		const frame = frames[frameIndex];
		if (frame) {
			image(frame, this.x, this.y);
		}
	}

	shouldRemove(): boolean {
		return this.isDestroyed;
	}
}
